import type { MailboxInfo } from 'owa-client-types';
import { getMailboxTypeForBizchatTelemetry } from 'owa-bizchat-account-utils';
import { OKRCategories } from './types/OKRCategories';
import { logCoreUsage } from 'owa-analytics';

type PrioritizeTelemetryEvent =
    | 'OpenInbox'
    | 'OpenSort'
    | 'OpenFilter'
    | 'EnabledChanged'
    | 'RulesCountChanged'
    | 'SummaryCardExpanded'
    | 'SummaryCardContentCopied'
    | 'PrioritizeSummary_Clicked'
    | 'EmailLabelCorrection'
    | 'PrioritizeButtonRendered'
    | 'PrioritizeViewOpened' // When Prioritize view successfully opens (after service work is completed)
    | 'PrioritizeTriggered' // When user initiates Prioritize by clicking the button/nudge and triggers service work
    | 'PrioritizeNudgeRendered'
    | 'PrioritizeNudgeDismissed'
    | 'PrioritizeNudgeTrigger'
    | 'PrioritizeNudgeDontShowAgain'
    | 'PrioritizeNudgeCustomizeInstructions';

type PrioritizeTelemetryEntryPoint = 'MessageList' | 'Settings' | 'ReadingPane';

function getOKRCategoryForEvent(
    event: PrioritizeTelemetryEvent,
    customData: {
        [key: string]: any;
    }
): OKRCategories {
    switch (event) {
        case 'OpenInbox':
        case 'OpenSort':
        case 'OpenFilter':
        case 'PrioritizeButtonRendered':
        case 'PrioritizeNudgeRendered':
            return OKRCategories.Seen;
        case 'EnabledChanged':
        case 'RulesCountChanged':
        case 'SummaryCardExpanded':
        case 'PrioritizeSummary_Clicked':
        case 'EmailLabelCorrection':
        case 'PrioritizeViewOpened':
            return OKRCategories.Tried;
        case 'PrioritizeTriggered':
            return customData.source === 'Auto' ? OKRCategories.None : OKRCategories.TriedInitiate;
        case 'SummaryCardContentCopied':
            return OKRCategories.Kept;
        case 'PrioritizeNudgeDismissed':
        case 'PrioritizeNudgeTrigger':
        case 'PrioritizeNudgeDontShowAgain':
        case 'PrioritizeNudgeCustomizeInstructions':
        default:
            return OKRCategories.None;
    }
}

function getEntryPointForEvent(
    event: PrioritizeTelemetryEvent
): PrioritizeTelemetryEntryPoint | undefined {
    switch (event) {
        case 'OpenInbox':
        case 'OpenSort':
        case 'OpenFilter':
        case 'PrioritizeButtonRendered':
        case 'PrioritizeViewOpened':
        case 'PrioritizeTriggered':
        case 'PrioritizeNudgeRendered':
        case 'PrioritizeNudgeDismissed':
        case 'PrioritizeNudgeDontShowAgain':
        case 'PrioritizeNudgeTrigger':
        case 'PrioritizeNudgeCustomizeInstructions':
            return 'MessageList';
        case 'EnabledChanged':
        case 'RulesCountChanged':
            return 'Settings';
        case 'SummaryCardExpanded':
        case 'SummaryCardContentCopied':
        case 'PrioritizeSummary_Clicked':
        case 'EmailLabelCorrection':
            return 'ReadingPane';
        default:
            return undefined;
    }
}

const PRIORITIZE_ON_DEMAND_SCENARIO = 'PrioritizeOnDemand';
const PRIORITIZE_SCENARIO = 'Prioritize';

function getScenarioForEvent(
    event: PrioritizeTelemetryEvent,
    customData: {
        [key: string]: any;
    }
): string {
    switch (event) {
        case 'PrioritizeTriggered':
        case 'PrioritizeNudgeDismissed':
            return PRIORITIZE_ON_DEMAND_SCENARIO;
        case 'PrioritizeButtonRendered':
        case 'PrioritizeViewOpened':
            return customData.isPrioritizeOnDeliveryOn
                ? PRIORITIZE_SCENARIO
                : PRIORITIZE_ON_DEMAND_SCENARIO;
        default:
            return PRIORITIZE_SCENARIO;
    }
}

function getActionForEvent(event: PrioritizeTelemetryEvent): string {
    switch (event) {
        case 'PrioritizeButtonRendered':
        case 'PrioritizeNudgeRendered':
            return 'Seen';
        case 'PrioritizeTriggered':
        case 'PrioritizeViewOpened':
            return 'Generate';
        default:
            return 'Other';
    }
}

export function logPrioritizeTelemetryEvent(
    event: PrioritizeTelemetryEvent,
    mailboxInfo: MailboxInfo,
    customData: {
        [key: string]: any;
    } = {}
): void {
    logCoreUsage(
        'AIStandardTelemetry',
        mailboxInfo,
        {
            CopilotEventName: event,
            OKRCategory: getOKRCategoryForEvent(event, customData),
            EntryPoint: getEntryPointForEvent(event),
            Action: getActionForEvent(event),
            Scenario: getScenarioForEvent(event, customData),
            MailboxProvider: mailboxInfo.mailboxProvider,
            MailboxType: getMailboxTypeForBizchatTelemetry(mailboxInfo),
            ...customData,
        },
        { isScorecardEvent: true }
    );
}
